import axios from 'axios';

import { LanguageTag, SupportedLocale } from '@flipster/i18n/types';
import { getServerHostname, isServer } from '@flipster/utils/env-helper';

const baseURL = `${getServerHostname()}/api`;
const timeout = 10 * 1000;

export const getAcceptLanguage = (locale: SupportedLocale): LanguageTag => {
  switch (locale) {
    case 'zh-CN':
      return 'zh-CN';
    case 'zh-TW':
      return 'zh-TW';
    case 'ru':
      return 'ru-RU';
    case 'vi':
      return 'vi-VN';
    default:
      return 'en-US';
  }
};

// HTML lang 기반으로 Accept-Language 헤더 설정
const getCurrentAcceptLanguage = (): LanguageTag => {
  if (typeof document !== 'undefined') {
    const htmlLang = document.documentElement.lang as SupportedLocale;
    if (['en', 'vi', 'zh-CN', 'zh-TW', 'ru'].includes(htmlLang)) {
      return getAcceptLanguage(htmlLang);
    }
  }
  return getAcceptLanguage('en');
};

const apiClient = axios.create({
  baseURL,
  timeout,
  withCredentials: true,
  headers: {
    'X-Prex-Client-Platform': 'web',
    'X-Prex-Client-Version': process.env.NEXT_PUBLIC_VERSION ?? '',
  },
});

// CORS Preflight 요청으로 인한 지연을 방지하기 위해 일부 API에 대해서는
// 몇가지 제약(Datadog 추적이 안되는 등)을 감수하고 Simple Request를 사용하기로 함
// https://developer.mozilla.org/en-US/docs/Web/HTTP/CORS#simple_requests
const simpleApiClient = axios.create({ baseURL, timeout });

[apiClient, simpleApiClient].forEach((client) => {
  client.interceptors.request.use((config) => {
    if (isServer) throw new Error('Cannot use axios on server side');

    // 매번 현재 HTML lang 기반으로 Accept-Language 헤더 설정
    config.headers['Accept-Language'] = getCurrentAcceptLanguage();

    return config;
  });
});

export { apiClient, simpleApiClient };
